import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Alert, Box, Stack } from '@mui/material';
import {
  dismissNotification,
  getNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  markNotificationUnread,
} from '../api/notificationsApi';
import { FilterBar, FilterSelect, FilterSelectOption } from '../components/FilterBar';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { NotificationsPanel } from '../components/NotificationsPanel';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { StatusBadge } from '../components/StatusBadge';
import { TableToolbar } from '../components/TableToolbar';
import { Button } from '../components/ui/Button';

const categories = ['account_security', 'support', 'billing', 'onboarding', 'system'];
const severities = ['info', 'success', 'warning', 'error', 'critical'];

export function NotificationsPage() {
  const { t } = useTranslation(['notifications', 'common']);
  const location = useLocation();
  const isPortal = location.pathname.startsWith('/portal');
  const preferencesPath = isPortal ? '/portal/notification-preferences' : '/notification-preferences';

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [markingAll, setMarkingAll] = useState(false);
  const [filters, setFilters] = useState({
    unread: '',
    category: '',
    severity: '',
  });

  const loadNotifications = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = {};
      if (filters.unread === 'true') {
        params.unread = true;
      }
      if (filters.category) {
        params.category = filters.category;
      }
      if (filters.severity) {
        params.severity = filters.severity;
      }

      const response = await getNotifications(params);
      setNotifications(response.data || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  const handleMarkRead = async (notification) => {
    await markNotificationRead(notification.id);
    await loadNotifications();
  };

  const handleMarkUnread = async (notification) => {
    await markNotificationUnread(notification.id);
    await loadNotifications();
  };

  const handleDismiss = async (notification) => {
    await dismissNotification(notification.id);
    await loadNotifications();
  };

  const handleMarkAllRead = async () => {
    setMarkingAll(true);
    try {
      await markAllNotificationsRead();
      await loadNotifications();
    } finally {
      setMarkingAll(false);
    }
  };

  const recordCount = notifications.length;
  const errorMessage = error?.response?.data?.message
    || error?.message
    || t('common:error');

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <PageHeader
        type="List"
        title={t('notifications:inbox.title')}
        subtitle={t('notifications:inbox.subtitle')}
        actions={(
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <Button
              variant="Secondary"
              component={RouterLink}
              to={preferencesPath}
            >
              {t('notifications:inbox.preferences')}
            </Button>
            <Button
              variant="Primary"
              onClick={handleMarkAllRead}
              loading={markingAll}
            >
              {t('notifications:inbox.mark_all_read')}
            </Button>
          </Stack>
        )}
      />

      <FilterBar type="Simple">
        <FilterSelect
          label={t('notifications:inbox.read_state')}
          hideLabel
          value={filters.unread}
          onChange={(e) => setFilters((prev) => ({ ...prev, unread: e.target.value }))}
          minWidth={160}
        >
          <FilterSelectOption value="">{t('common:all')}</FilterSelectOption>
          <FilterSelectOption value="true">{t('notifications:inbox.unread')}</FilterSelectOption>
        </FilterSelect>
        <FilterSelect
          label={t('notifications:inbox.category')}
          hideLabel
          value={filters.category}
          onChange={(e) => setFilters((prev) => ({ ...prev, category: e.target.value }))}
          minWidth={180}
        >
          <FilterSelectOption value="">{t('common:all')}</FilterSelectOption>
          {categories.map((category) => (
            <FilterSelectOption key={category} value={category}>
              {t(`notifications:preferences.categories.${category}`, { defaultValue: category })}
            </FilterSelectOption>
          ))}
        </FilterSelect>
        <FilterSelect
          label={t('notifications:inbox.severity')}
          hideLabel
          value={filters.severity}
          onChange={(e) => setFilters((prev) => ({ ...prev, severity: e.target.value }))}
          minWidth={160}
        >
          <FilterSelectOption value="">{t('common:all')}</FilterSelectOption>
          {severities.map((severity) => (
            <FilterSelectOption key={severity} value={severity}>
              {t(`notifications:inbox.severities.${severity}`, { defaultValue: severity })}
            </FilterSelectOption>
          ))}
        </FilterSelect>
      </FilterBar>

      {error && !loading && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={(
            <Button variant="Ghost" onClick={loadNotifications} sx={{ minHeight: 32 }}>
              {t('common:retry')}
            </Button>
          )}
        >
          {errorMessage}
        </Alert>
      )}

      <SectionCard variant="Default" noPadding sx={{ width: '100%' }}>
        <TableToolbar
          mode="Default"
          title={t('notifications:inbox.list')}
          meta={(
            <StatusBadge
              status="list"
              category="generic"
              apiLabel={t('notifications:inbox.toolbar_count', { count: recordCount })}
              size="Sm"
              tone="Neutral"
            />
          )}
        />

        {loading ? (
          <Box sx={{ p: 2.5 }}>
            <LoadingSkeleton type="CardGrid" rows={3} />
          </Box>
        ) : (
          <Box sx={{ p: 2.5, pt: 0 }}>
            <NotificationsPanel
              notifications={notifications}
              onMarkRead={handleMarkRead}
              onMarkUnread={handleMarkUnread}
              onDismiss={handleDismiss}
            />
          </Box>
        )}
      </SectionCard>
    </Box>
  );
}
